/**
 * web-dispatch — every hand-written role-code UX gate in one place (quick 261012-jiy).
 *
 * These are UX gates only (which nav sections, buttons and panels a role sees); the API is
 * authoritative. They mirror packages/api/src/auth/role-gates.ts.
 *
 * DEC-313 union rule: a combined role is in a gate IFF at least one of its two components is.
 * `withCombinedRoles` enforces that by derivation from COMBINED_ROLE_COMPONENTS — the definition
 * of record ROLE_PERMISSIONS is built from — so a DISPATCH_MANAGER_BILLING_STAFF sees exactly
 * what its two halves see, never a hidden button where a plain DISPATCH_MANAGER has one.
 *
 * RULES FOR THIS FILE
 * - List the single roles, never the combined ones (the parameter is typed SingleRoleCode[]).
 * - Every role-code gate in web-dispatch MUST be defined here and registered in
 *   WEB_DISPATCH_ROLE_GATES; lib/__tests__/role-gates.test.ts asserts the union rule over that
 *   map and its exact size, and sweeps src for hand-written component-code gates.
 * - Import nothing from features/ (no cycles: features import from here).
 *
 * Why not in packages/shared: a git worktree resolves `@lodex-pro/shared` to the MAIN
 * checkout's source, so a new shared export is invisible to tsc/vitest until it is on main.
 * Hoisting this helper (and the api copy) into packages/shared is a follow-up.
 */

import { COMBINED_ROLE_COMPONENTS, ROLE_PERMISSIONS } from '@lodex-pro/shared/auth/permissions';
import type { CombinedRoleCode, SingleRoleCode } from '@lodex-pro/shared/auth/role-codes';

/**
 * Every role (single and combined) whose ROLE_PERMISSIONS entry holds `permission`. A gate built
 * this way mirrors an API route's `config.permission` exactly, so it cannot drift from it, and it
 * already satisfies the DEC-313 union rule (a combined role's permissions are the union of its
 * components').
 */
export function rolesWithPermission(permission: string): ReadonlySet<string> {
  return new Set(
    Object.entries(ROLE_PERMISSIONS)
      .filter(([, perms]) => (perms as ReadonlySet<string>).has(permission))
      .map(([code]) => code),
  );
}

/**
 * The given single roles plus every DEC-313 combined role with at least one component among
 * them. Returns a new Set; never adds a combined role neither of whose components is listed.
 */
export function withCombinedRoles(singles: readonly SingleRoleCode[]): ReadonlySet<string> {
  const out = new Set<string>(singles);
  for (const [combined, components] of Object.entries(COMBINED_ROLE_COMPONENTS) as [
    CombinedRoleCode,
    readonly [SingleRoleCode, SingleRoleCode],
  ][]) {
    if (components.some((c) => singles.includes(c))) out.add(combined);
  }
  return out;
}

/**
 * Roles that may manage reference data — the Management, Sites & Operators, Fleet and
 * Operator Changes nav gate (App.tsx), and the manage affordances on the Settings
 * sections (features/settings), SetupWizard and SitesOperatorsPage. The read tabs are still server-gated.
 */
export const MANAGE_ROLES = withCombinedRoles([
  'DISPATCH_MANAGER',
  'TENANT_ADMIN',
  'PLATFORM_ADMIN',
]);

/** Roles that get the new-hauler wizard gate (run the wizard) — NOT plain dispatchers. */
export const WIZARD_GATE_ROLES = withCombinedRoles(['TENANT_ADMIN', 'DISPATCH_MANAGER']);

/**
 * TenantGeofenceModePanel — the tenant geofence-mode <select> is editable for
 * DISPATCH_MANAGER+ only; the server PATCH is the authoritative guard.
 */
export const GEOFENCE_MODE_ROLES = withCombinedRoles([
  'DISPATCH_MANAGER',
  'TENANT_ADMIN',
  'PLATFORM_ADMIN',
]);

/**
 * Who may open Settings → Import (DEC-256). §80 lists Dispatcher too; DEC-256 drops it — the
 * re-import default is UPDATE, so the permission could bulk-overwrite the customer list every
 * job bills against. Mirrors the API's MANAGE_HAULER_CATALOG holders; the API is the real gate.
 */
export const IMPORT_ROLES = withCombinedRoles([
  'TENANT_ADMIN',
  'DISPATCH_MANAGER',
  'PLATFORM_ADMIN',
]);

/**
 * JobDetailModal — who gets an ENABLED Cancel Job button on a PENDING / EN_ROUTE_TO_PICKUP job
 * (DEC-122 / DEC-123). Mirrors MANAGER_CANCEL_ROLES in packages/api/src/auth/role-gates.ts.
 */
export const MANAGER_CANCEL_ROLES = withCombinedRoles([
  'DISPATCH_MANAGER',
  'TENANT_ADMIN',
  'PLATFORM_ADMIN',
]);

/**
 * JobDetailModal — who SEES the Cancel Job button at all. Enabled for MANAGER_CANCEL_ROLES;
 * a DISPATCHER (or DISPATCHER_BILLING_*) sees it disabled with an explanatory tooltip, except
 * on a "Not started" job (OPEN-117), where it is enabled. Same membership as, and so mirrors,
 * NOT_STARTED_CANCEL_ROLES in packages/api/src/auth/role-gates.ts.
 */
export const CANCEL_BUTTON_ROLES = withCombinedRoles([
  'DISPATCHER',
  'DISPATCH_MANAGER',
  'TENANT_ADMIN',
  'PLATFORM_ADMIN',
]);

/** JobQueue — who sees the "Force Assign Back" menu item. Mirrors api FORCE_ASSIGN_ROLES. */
export const FORCE_ASSIGN_ROLES = withCombinedRoles([
  'DISPATCH_MANAGER',
  'TENANT_ADMIN',
  'PLATFORM_ADMIN',
]);

/**
 * SiteDetailModal — MANAGE_HAULER_CATALOG is DISPATCH_MANAGER+ (server-authoritative; this is
 * a UX gate only, e.g. renaming a tank battery).
 */
export const MANAGE_CATALOG_ROLES = withCombinedRoles([
  'DISPATCH_MANAGER',
  'TENANT_ADMIN',
  'PLATFORM_ADMIN',
]);

/**
 * InReviewDetailPanel — who may edit the reviewed volume (server authoritative on
 * EDIT_TICKET_VOLUME). DEC-125 / §6: TRUCK_PUSHER, BILLING_MANAGER, TENANT_ADMIN, PLATFORM_ADMIN.
 */
export const EDIT_VOLUME_ROLES = withCombinedRoles([
  'TRUCK_PUSHER',
  'BILLING_MANAGER',
  'TENANT_ADMIN',
  'PLATFORM_ADMIN',
]);

/** OperationsPage — roles holding REVIEW_TICKET (§6): who sees the InReview queue tab. */
export const REVIEW_ROLES = withCombinedRoles([
  'PLATFORM_ADMIN',
  'TENANT_ADMIN',
  'DISPATCH_MANAGER',
  'TRUCK_PUSHER',
  'BILLING_MANAGER',
]);

/**
 * Stuck jobs — who may force-close a driver's clock. Mirrors FORCE_CLOSE_ROLES in
 * packages/api/src/auth/role-gates.ts (dispatcher-close), which now admits the combined
 * DISPATCH_MANAGER roles too.
 */
export const FORCE_CLOSE_ROLES = withCombinedRoles([
  'PLATFORM_ADMIN',
  'TENANT_ADMIN',
  'DISPATCH_MANAGER',
  'TRUCK_PUSHER',
]);

/**
 * Stuck jobs — who may cancel a STUCK in-progress job (DEC-315 option B). Mirrors
 * STUCK_CANCEL_ROLES in packages/api/src/auth/role-gates.ts. DISPATCHER_BILLING_* stay out
 * because neither of their components is listed.
 */
export const STUCK_CANCEL_ROLES = withCombinedRoles([
  'DISPATCH_MANAGER',
  'TENANT_ADMIN',
  'PLATFORM_ADMIN',
]);

/**
 * Operations dashboard — Phase 19 / Task 19.2 (DEC-325 D-2). Mirrors the API gate on
 * GET /v1/dashboards/operations, which is ASSIGN_JOB: exactly these four roles plus the
 * combined roles derived from DISPATCHER / DISPATCH_MANAGER.
 */
export const OPS_DASHBOARD_ROLES = withCombinedRoles([
  'PLATFORM_ADMIN',
  'TENANT_ADMIN',
  'DISPATCH_MANAGER',
  'DISPATCHER',
]);

/**
 * Management dashboard — Phase 19 / Task 19.3 (DEC-325 D-2). Mirrors the API gate on
 * GET /v1/dashboards/management, which is MANAGE_TENANT: tenant and platform admins only.
 * Neither is a component of any combined role, so the DEC-313 union rule has nothing to add:
 * a plain Set, deliberately NOT in WEB_DISPATCH_ROLE_GATES (whose test requires every gate to
 * contain a component, so its union check is not vacuous).
 */
export const MGMT_DASHBOARD_ROLES: ReadonlySet<string> = new Set([
  'TENANT_ADMIN',
  'PLATFORM_ADMIN',
]);

/**
 * Extra Services admin in Settings → Jobs, products & pricing (OPEN-111). Mirrors the API gate on
 * POST/PUT /v1/service-charges, which is MANAGE_SERVICE_CHARGES. Derived from
 * ROLE_PERMISSIONS rather than listed, so it cannot drift from the API: today that is
 * TENANT_ADMIN alone. PLATFORM_ADMIN does NOT hold it, so showing it the editor would only
 * produce a 403 on save. No combined-role component holds it either, so like
 * MGMT_DASHBOARD_ROLES it is NOT in WEB_DISPATCH_ROLE_GATES (and the derivation already
 * satisfies the DEC-313 union rule, since combined roles' permission sets are unions).
 */
export const MANAGE_SERVICE_CHARGES_ROLES = rolesWithPermission('MANAGE_SERVICE_CHARGES');

/**
 * OPEN-217 (DEC-382) — Settings → Driver entry rules: the company's checks on what drivers type
 * (/v1/validation-rules, MANAGE_VALIDATION_RULES). Admin and billing staff edit them, never
 * dispatch. Derived from the permission, so like MANAGE_SERVICE_CHARGES_ROLES it is NOT in
 * WEB_DISPATCH_ROLE_GATES.
 */
export const MANAGE_VALIDATION_RULES_ROLES = rolesWithPermission('MANAGE_VALIDATION_RULES');

/**
 * Settings (quick 261013-srg PR 2) — the MANAGE_TENANT holders: today TENANT_ADMIN and
 * PLATFORM_ADMIN. The API gates every company-wide setting on it (review rules and completion
 * policy PATCH, print, load emails, idle-shift close, running costs, on-time rule, accounting
 * export, site types), so only these roles see those sections or get an editable control.
 * Replaces SettingsPage's SETTINGS_ADMIN_ROLES and SetupConfigPage's TENANT_ADMIN_ROLES. Admin
 * roles only, so like MGMT_DASHBOARD_ROLES it is NOT in WEB_DISPATCH_ROLE_GATES.
 */
export const SETTINGS_ADMIN_ROLES = rolesWithPermission('MANAGE_TENANT');

/**
 * Settings → Ticket review & approvals, read-only (quick 261013-srg decision 3: dispatch
 * managers VIEW, not change, the review rules and the completion policy). Mirrors
 * GET /v1/tenants/inreview-settings, which is MANAGE_REFERENCE_DATA.
 */
export const REVIEW_RULES_VIEW_ROLES = rolesWithPermission('MANAGE_REFERENCE_DATA');

/**
 * Settings → API connections (operator portal rework PR 9): the read-only list of operator API
 * keys that read this hauler's data. Mirrors GET /v1/operator-api-credentials, which is
 * VIEW_OPERATOR_API_CREDENTIALS: TENANT_ADMIN alone today (PLATFORM_ADMIN does not hold it).
 * Derived from the permission, so like MANAGE_SERVICE_CHARGES_ROLES it is NOT in
 * WEB_DISPATCH_ROLE_GATES.
 */
export const API_CONNECTIONS_VIEW_ROLES = rolesWithPermission('VIEW_OPERATOR_API_CREDENTIALS');

/** Settings → Team & access. Mirrors the /v1/users routes, which are MANAGE_USERS. */
export const MANAGE_USERS_ROLES = rolesWithPermission('MANAGE_USERS');

/**
 * OPEN-036 / DEC-335 — the Tablets page (list shared tablets, "Sign out now"). Mirrors the API
 * gate on GET /v1/devices and POST /v1/devices/:id/clear, which is CLEAR_SHARED_DEVICE. Derived
 * from ROLE_PERMISSIONS (rolesWithPermission) like MANAGE_SERVICE_CHARGES_ROLES, so it cannot drift from the API:
 * DISPATCHER, DISPATCH_MANAGER, TENANT_ADMIN, PLATFORM_ADMIN, and the combined roles built from
 * DISPATCHER / DISPATCH_MANAGER (their permission sets are unions, so the DEC-313 rule holds by
 * construction). Not in WEB_DISPATCH_ROLE_GATES for the same reason.
 */
export const TABLET_ROLES = rolesWithPermission('CLEAR_SHARED_DEVICE');

/**
 * OPEN-035 — who sees "Add a tablet" on the Tablets page. Mirrors the API gate on
 * POST /v1/tenants/:id/devices/enroll, which is MANAGE_USERS (master spec §7: "Tenant admin
 * enrolls device"): TENANT_ADMIN, PLATFORM_ADMIN, DISPATCH_MANAGER and the combined roles built
 * from DISPATCH_MANAGER. A plain DISPATCHER can sign a tablet out but not enroll one. Every
 * holder also holds CLEAR_SHARED_DEVICE, so every holder can reach the page (pinned by test).
 */
export const TABLET_ENROLL_ROLES = rolesWithPermission('MANAGE_USERS');

/**
 * DEC-328 — who may "Ask driver to sync". Mirrors the ASSIGN_JOB holders that gate
 * POST /v1/jobs/:id/sync-nudge in the API: the whole dispatch tier, and every combined
 * role through its dispatch component.
 */
export const SYNC_NUDGE_ROLES = withCombinedRoles([
  'DISPATCHER',
  'DISPATCH_MANAGER',
  'TENANT_ADMIN',
  'PLATFORM_ADMIN',
]);

/**
 * DEC-329 — who may ask for (or, under ALWAYS, make) a dispatcher completion: "Send to
 * review" on the stuck list. Mirrors COMPLETION_REQUEST_ROLES in packages/api/src/auth/role-gates.ts.
 */
export const COMPLETION_REQUEST_ROLES = withCombinedRoles([
  'DISPATCHER',
  'DISPATCH_MANAGER',
  'TENANT_ADMIN',
  'PLATFORM_ADMIN',
]);

/**
 * DEC-329 — who approves or declines a request, and whose own "Send to review" goes straight
 * through under SUPERVISOR_REQUIRED. Mirrors COMPLETION_APPROVER_ROLES in the API.
 */
export const COMPLETION_APPROVER_ROLES = withCombinedRoles([
  'DISPATCH_MANAGER',
  'TENANT_ADMIN',
  'PLATFORM_ADMIN',
]);

/**
 * OPEN-157 B — who sees "Take back" on a started job: DISPATCH_MANAGER and up. Mirrors
 * TAKE_BACK_ROLES in the API (the server decides; this only hides the button).
 */
export const TAKE_BACK_ROLES = withCombinedRoles([
  'DISPATCH_MANAGER',
  'TENANT_ADMIN',
  'PLATFORM_ADMIN',
]);

/**
 * Registry — every role-code gate in web-dispatch MUST be defined above and listed here.
 * lib/__tests__/role-gates.test.ts asserts the DEC-313 union rule over this map and its size.
 */
export const WEB_DISPATCH_ROLE_GATES: Readonly<Record<string, ReadonlySet<string>>> = {
  MANAGE_ROLES,
  WIZARD_GATE_ROLES,
  GEOFENCE_MODE_ROLES,
  IMPORT_ROLES,
  MANAGER_CANCEL_ROLES,
  CANCEL_BUTTON_ROLES,
  FORCE_ASSIGN_ROLES,
  MANAGE_CATALOG_ROLES,
  EDIT_VOLUME_ROLES,
  REVIEW_ROLES,
  FORCE_CLOSE_ROLES,
  STUCK_CANCEL_ROLES,
  OPS_DASHBOARD_ROLES,
  SYNC_NUDGE_ROLES,
  COMPLETION_REQUEST_ROLES,
  COMPLETION_APPROVER_ROLES,
  REVIEW_RULES_VIEW_ROLES,
  MANAGE_USERS_ROLES,
  TAKE_BACK_ROLES,
};

/**
 * #52 (DEC-358) — who can save a customer disposal fee (web-billing's fee page, the
 * MANAGE_DISPOSAL_PRICES routes). In Review offers "Set fee now" to these roles; others are
 * told to ask billing. Derived from the permission, so like MANAGE_SERVICE_CHARGES_ROLES it
 * is NOT in WEB_DISPATCH_ROLE_GATES.
 */
export const MANAGE_DISPOSAL_FEE_ROLES = rolesWithPermission('MANAGE_DISPOSAL_PRICES');

/**
 * gap 1 / DEC-383 — the customer price editors (billing staff, billing managers, admins and the
 * combined roles). Mirrors the MANAGE_CUSTOMER_PRICES routes; UX only. Derived from the
 * permission, so like MANAGE_DISPOSAL_FEE_ROLES it is NOT in WEB_DISPATCH_ROLE_GATES.
 */
export const MANAGE_CUSTOMER_PRICES_ROLES = rolesWithPermission('MANAGE_CUSTOMER_PRICES');

/**
 * gap 1 D-20 — who gets the In Review "price for this job type" fix: MANAGE_CUSTOMER_PRICES AND
 * REVIEW_TICKET (POST /v1/inreview/:jobId/job-type-price checks both). Dispatch managers keep only
 * the general-rate fix; billing staff do not review. An intersection of two permission-derived
 * sets, so the DEC-313 union rule holds by construction; NOT in WEB_DISPATCH_ROLE_GATES.
 */
const REVIEW_TICKET_HOLDERS = rolesWithPermission('REVIEW_TICKET');
export const JOB_TYPE_PRICE_FIX_ROLES: ReadonlySet<string> = new Set(
  Array.from(MANAGE_CUSTOMER_PRICES_ROLES).filter((r) => REVIEW_TICKET_HOLDERS.has(r)),
);

/**
 * #60 (DEC-365) — who sees "Change site" and the tank re-pick, on the job detail and in In
 * Review. Mirrors the CHANGE_JOB_SITE routes (dispatch, both reviewers, the admins). Derived
 * from the permission, so like MANAGE_DISPOSAL_FEE_ROLES it is NOT in WEB_DISPATCH_ROLE_GATES.
 */
export const CHANGE_SITE_ROLES = rolesWithPermission('CHANGE_JOB_SITE');
